// ATN Digital Showroom - Hero Slider
// Pointer Events + requestAnimationFrame drag (smooth, no scroll lock)

class ATNSRHeroSlider {
  constructor(section) {
    this.section = section;
    this.root = section.querySelector('[data-atn-sr-hero]');
    this.viewport = section.querySelector('[data-hero-viewport]');
    this.track = section.querySelector('[data-hero-track]');
    this.slides = Array.from(section.querySelectorAll('[data-hero-slide]'));
    this.prevBtn = section.querySelector('[data-hero-prev]');
    this.nextBtn = section.querySelector('[data-hero-next]');
    this.dots = Array.from(section.querySelectorAll('[data-hero-dot]'));

    if (!this.root || !this.viewport || !this.track || this.slides.length === 0) return;

    this.autoplay = this.root.dataset.autoplay === 'true';
    this.speed = (parseInt(this.root.dataset.speed, 10) || 6) * 1000;
    this.loop = this.root.dataset.loop === 'true';

    this.index = 0;
    this.width = this.viewport.clientWidth;
    this.dragging = false;
    this.axisLocked = false;
    this.isHorizontal = false;
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.delta = 0;
    this.rafId = null;
    this.timer = null;
    this.suppressClick = false;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.bind();
    this.jumpTo(0);
    if (this.autoplay) this.startAutoplay();
  }

  /* ---------- setup ---------- */

  bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev(true));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next(true));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));

    // Drag only makes sense with more than one slide
    if (this.slides.length > 1 && !window.PointerEvent) {
      // Legacy fallback: map touch events onto the same handlers
      const toPointer = (handler) => (e) => {
        const touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        handler({
          pointerId: touch.identifier,
          pointerType: 'touch',
          clientX: touch.clientX,
          clientY: touch.clientY,
          target: e.target,
          button: 0
        });
      };
      this.viewport.addEventListener('touchstart', toPointer(this.onPointerDown), { passive: true });
      this.viewport.addEventListener('touchmove', toPointer(this.onPointerMove), { passive: true });
      this.viewport.addEventListener('touchend', toPointer(this.onPointerUp));
      this.viewport.addEventListener('touchcancel', toPointer(this.onPointerCancel));
      this.viewport.addEventListener('click', this.onClickCapture, true);
    } else if (this.slides.length > 1) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
      this.viewport.addEventListener('click', this.onClickCapture, true);
    }

    this.viewport.addEventListener('mouseenter', () => this.stopAutoplay());
    this.viewport.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });

    this.viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.next(true);
      if (e.key === 'ArrowLeft') this.prev(true);
    });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /* ---------- drag ---------- */

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('a, button')) return;

    this.pointerId = e.pointerId;
    this.pointerType = e.pointerType;
    this.hasCapture = false;
    this.dragging = true;
    this.axisLocked = false;
    this.isHorizontal = false;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.delta = 0;
    this.width = this.viewport.clientWidth;
    this.stopAutoplay();
  }

  onPointerMove(e) {
    if (!this.dragging || e.pointerId !== this.pointerId) return;

    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    // Decide the gesture direction once, then stick with it
    if (!this.axisLocked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      this.axisLocked = true;
      this.isHorizontal = Math.abs(dx) > Math.abs(dy);

      if (!this.isHorizontal) {
        // Vertical gesture: hand it back to the page scroll
        this.resetDrag();
        return;
      }

      this.track.classList.add('is-dragging');
      this.viewport.classList.add('is-dragging');

      // Touch already has implicit capture on the pointerdown target.
      // Re-capturing there breaks the gesture, so only capture mouse/pen.
      if (this.pointerType !== 'touch' && this.viewport.setPointerCapture) {
        try {
          this.viewport.setPointerCapture(this.pointerId);
          this.hasCapture = true;
        } catch (err) {
          this.hasCapture = false;
        }
      }
    }

    if (!this.isHorizontal) return;

    this.delta = this.applyResistance(dx);
    this.render();
  }

  onPointerUp(e) {
    if (!this.dragging || e.pointerId !== this.pointerId) return;

    const moved = this.delta;
    const wasHorizontal = this.isHorizontal;
    this.resetDrag();

    if (!wasHorizontal) return;

    // Block the click that follows a real swipe
    if (Math.abs(moved) > 8) this.suppressClick = true;

    const threshold = Math.max(40, this.width * 0.14);
    if (moved <= -threshold) {
      this.next();
    } else if (moved >= threshold) {
      this.prev();
    } else {
      this.jumpTo(this.index, true);
    }

    if (this.autoplay) this.startAutoplay();
  }

  onPointerCancel(e) {
    if (!this.dragging || (e && e.pointerId !== this.pointerId)) return;
    const wasHorizontal = this.isHorizontal;
    this.resetDrag();
    if (wasHorizontal) this.jumpTo(this.index, true);
    if (this.autoplay) this.startAutoplay();
  }

  resetDrag() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.hasCapture && this.pointerId !== null && this.viewport.releasePointerCapture) {
      try {
        this.viewport.releasePointerCapture(this.pointerId);
      } catch (err) {
        /* nothing to release */
      }
    }
    this.hasCapture = false;
    this.dragging = false;
    this.axisLocked = false;
    this.isHorizontal = false;
    this.pointerId = null;
    this.delta = 0;
    this.track.classList.remove('is-dragging');
    this.viewport.classList.remove('is-dragging');
  }

  applyResistance(dx) {
    if (this.loop) return dx;
    const atStart = this.index === 0 && dx > 0;
    const atEnd = this.index === this.slides.length - 1 && dx < 0;
    return atStart || atEnd ? dx * 0.32 : dx;
  }

  onClickCapture(e) {
    if (!this.suppressClick) return;
    this.suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  }

  /* ---------- movement ---------- */

  render() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      const offset = -this.index * this.width + this.delta;
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    });
  }

  jumpTo(index, animate = true) {
    this.index = index;
    this.width = this.viewport.clientWidth;

    if (!animate) {
      this.track.classList.add('is-noanim');
      this.track.style.transform = `translate3d(${-this.index * this.width}px, 0, 0)`;
      void this.track.offsetWidth; // force reflow before re-enabling the transition
      this.track.classList.remove('is-noanim');
    } else {
      this.track.style.transform = `translate3d(${-this.index * this.width}px, 0, 0)`;
    }

    this.updateUI();
  }

  goTo(index, restartAutoplay = false) {
    let target = index;
    const last = this.slides.length - 1;

    if (target < 0) target = this.loop ? last : 0;
    if (target > last) target = this.loop ? 0 : last;

    this.jumpTo(target, true);
    if (restartAutoplay && this.autoplay) this.restartAutoplay();
  }

  next(restartAutoplay = false) {
    this.goTo(this.index + 1, restartAutoplay);
  }

  prev(restartAutoplay = false) {
    this.goTo(this.index - 1, restartAutoplay);
  }

  updateUI() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
    });

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
    } else {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    }
  }

  onResize() {
    const width = this.viewport.clientWidth;
    if (width === this.width) return;
    this.jumpTo(this.index, false);
  }

  /* ---------- autoplay ---------- */

  startAutoplay() {
    if (!this.autoplay || this.timer || this.slides.length < 2) return;
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stopAutoplay() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  onVisibility() {
    if (document.hidden) {
      this.stopAutoplay();
    } else if (this.autoplay) {
      this.startAutoplay();
    }
  }

  destroy() {
    this.stopAutoplay();
    this.resetDrag();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}

/* ---------- init ---------- */

function initATNSRHeroSliders(scope) {
  const root = scope || document;
  root.querySelectorAll('[data-atn-sr-hero]').forEach((element) => {
    const section = element.closest('.atn-sr-hero-section') || element.parentElement;
    if (!section || section.atnSrHero) return;
    section.atnSrHero = new ATNSRHeroSlider(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initATNSRHeroSliders());
} else {
  initATNSRHeroSliders();
}

// Theme editor support
document.addEventListener('shopify:section:load', (event) => {
  initATNSRHeroSliders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('.atn-sr-hero-section') || event.target;
  if (section && section.atnSrHero) {
    section.atnSrHero.destroy();
    section.atnSrHero = null;
  }
});